import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { apiGet, apiPost, apiPut, getErrorMessage } from '../utils/api'
import { useAudioAlerts } from './useAudioAlerts'
import { AuthContext } from '../contexts/AuthContext'

export interface ChatAttachment {
  id: string
  url: string
  filename: string
  type: 'image' | 'video' | 'file'
  size: number
}

export interface ChatMessage {
  id: string
  chatId: string
  sender: 'customer' | 'agent' | 'bot' | 'whisper' | 'system'
  senderName: string
  senderAvatar?: string
  content: string
  attachments: ChatAttachment[]
  isInternal: boolean
  createdAt: string
  readAt?: string
  /**
   * Original message_type from the API. When `'html'`, `content` holds
   * server-sanitized HTML (rich agent replies) and should be rendered as HTML;
   * any other value is plain text. Defaults to plain text when undefined.
   */
  messageType?: string
  /** True when this message has an edit history (agent-side only). */
  edited?: boolean
  /** Sender's agent id — used to gate "you can only edit your own messages". */
  senderId?: string
}

export interface MessageVersion {
  id: number | string
  body: string
  edited_by_name?: string | null
  created_at: string
}

export interface ChatDetail {
  id: string
  customerId: string
  customerName: string
  customerPhone?: string
  channel?: string
  status: 'open' | 'closed' | 'missed' | 'reopened'
  priority: 'low' | 'medium' | 'high'
  tags: string[]
  messages: ChatMessage[]
  notes: Array<{ id: string; content: string; authorName: string; createdAt: string }>
  createdAt: string
  closedAt?: string
  resolutionTag?: string
  // Who is currently handling the chat (updates live on pickup/transfer)
  assignedAgentId?: string
  assignedAgentName?: string
  assignedAgentAvatar?: string
  // AI First Responder state — when 'active', Jesse is handling the chat and an
  // observing agent should NOT get a per-message ding.
  aiState?: string | null
  // Phase 1: New fields
  sourceIp?: string
  missedAt?: string
  missedReason?: string
  departmentId?: string
  firstAgentId?: string
  firstResponseAt?: string
  queueInAt?: string
  queueOutAt?: string
  visitorBrowser?: string
  visitorOs?: string
  landingPage?: string
  currentPage?: string
}

interface UseChatThreadReturn {
  chat: ChatDetail | null
  messages: ChatMessage[]
  loading: boolean
  error: string | null
  typingIndicator: string | null
  typingPreview: string | null
  suggestedReply: string | null
  sendMessage: (content: string, attachments?: any[]) => Promise<void>
  editMessage: (messageId: string, body: string) => Promise<void>
  getMessageVersions: (messageId: string) => Promise<MessageVersion[]>
  suggestReply: () => Promise<void>
  closeChat: (resolutionTag: string, notes?: string) => Promise<void>
  addNote: (content: string) => Promise<void>
  acceptSuggestion: () => Promise<void>
  discardSuggestion: () => Promise<void>
  /** Emit typing_start to the socket so the widget shows a typing indicator */
  startTyping: () => void
  /** Emit typing_stop to the socket */
  stopTyping: () => void
}

export const useChatThread = (chatId?: string): UseChatThreadReturn => {
  // For attributing MY sent messages (name/avatar/id on the bubble) without
  // waiting for a server round-trip.
  const authCtx = useContext(AuthContext)
  const me = authCtx?.currentAgent ?? null
  const [chat, setChat] = useState<ChatDetail | null>(null)
  // When the open chat is AI-handled, an observing agent shouldn't get a ding on
  // every customer message. Ref so the socket handler reads the latest value.
  const aiActiveRef = useRef(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [typingIndicator, setTypingIndicator] = useState<string | null>(null)
  const [typingPreview, setTypingPreview] = useState<string | null>(null)
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Chat switched → drop the PREVIOUS chat's transient typing state ─────────
  // ChatThread is not keyed by chatId, so this hook's state is REUSED when you
  // move between chats. Without this reset, the last customer's live typing
  // preview stayed in state and rendered underneath the NEXT customer's chat —
  // which is exactly the "someone else's preview on this chat" bug.
  useEffect(() => {
    setTypingPreview(null)
    setTypingIndicator(null)
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
  }, [chatId])

  // Clear any pending expiry timer on unmount.
  useEffect(() => () => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
  }, [])
  const [suggestedReply, setSuggestedReply] = useState<string | null>(null)
  const { socket } = useSocket()
  const { playMessageAlert } = useAudioAlerts()

  const fetchChat = useCallback(async () => {
    if (!chatId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const [chatData, messagesData] = await Promise.all([
        apiGet<any>(`/chats/${chatId}`),
        apiGet<any>(`/chats/${chatId}/messages`),
      ])

      // Map snake_case API fields to camelCase
      const rawMessages = Array.isArray(messagesData) ? messagesData : (messagesData.messages || [])
      // Who's handling this chat — the messages endpoint resolves the assigned
      // agent's name + avatar (falls back to the chat's agent_id otherwise).
      const assignedAgent = (!Array.isArray(messagesData) && messagesData.assigned_agent) || null
      const mappedMessages: ChatMessage[] = rawMessages.map((m: any) => ({
        id: m.id,
        chatId: m.chat_id || chatId,
        // System events (pickup/transfer) carry message_type='system' — render
        // them as a status line, not a customer bubble.
        sender: (m.message_type === 'system' || m.sender_type === 'system') ? 'system' : (m.sender_type || m.sender || 'customer'),
        senderName: (m.message_type === 'system' || m.sender_type === 'system') ? '' : (m.sender_name || (m.sender_type === 'agent' ? 'Agent' : 'Customer')),
        content: m.body || m.content || '',
        edited: !!m.edited,
        senderId: m.sender_id != null ? String(m.sender_id) : undefined,
        senderAvatar: m.sender_avatar || m.senderAvatar || undefined,
        attachments: (m.attachments || []).map((a: any) => ({
          id: a.id,
          url: a.url,
          filename: a.name || a.filename || 'file',
          type: a.type || 'file',
          size: a.size || 0,
        })),
        isInternal: m.is_internal || false,
        createdAt: m.created_at || m.createdAt,
        readAt: m.read_at || m.readAt,
      }))

      // Build customer display name — prefer display_name, fall back to phone for WhatsApp
      const custPhone = chatData.customer?.phone || chatData.wa_from || null;
      const custDisplayName = chatData.customer?.display_name || chatData.customer?.name;
      // Prefer the actual person who started THIS chat (visitor_name, captured
      // from the widget identity) — same name the chat list shows — before
      // falling back to the linked customer record or the "Account WQ-…" label.
      let customerName = chatData.visitor_name || custDisplayName || `Account ${chatData.customer?.wequote_account_id || 'Unknown'}`;
      // For WhatsApp chats, append phone if it's not already in the name
      if (chatData.channel === 'whatsapp' && custPhone && !customerName.includes(custPhone)) {
        customerName = `${customerName} (+${custPhone})`;
      }

      const mappedChat: ChatDetail = {
        id: chatData.id,
        customerId: chatData.customer_id || chatData.customerId,
        customerName,
        customerPhone: custPhone,
        channel: chatData.channel,
        status: chatData.status,
        aiState: chatData.ai_state ?? null,
        priority: chatData.priority || 'medium',
        tags: Array.isArray(chatData.tags) ? chatData.tags : [],
        messages: mappedMessages,
        // Notes come back as { body, agent_name, created_at } — map to the
        // UI shape { content, authorName, createdAt } or they render blank.
        notes: (chatData.notes || []).map((n: any) => ({
          id: String(n.id),
          content: n.content || n.body || '',
          authorName: n.authorName || n.agent_name || 'Agent',
          createdAt: n.createdAt || n.created_at,
        })),
        createdAt: chatData.created_at || chatData.createdAt,
        closedAt: chatData.closed_at || chatData.closedAt,
        // The resolution tag lives in the `tags` JSON array — closeChat stores
        // it as [resolutionTag] (see chat.service.closeChat). resolution_summary
        // is the free-text notes, NOT the tag, so reading it here put the notes
        // where the tag should be.
        resolutionTag: Array.isArray(chatData.tags) && chatData.tags.length > 0
          ? chatData.tags[0]
          : (chatData.resolutionTag || undefined),
        assignedAgentId: assignedAgent?.agent_id != null ? String(assignedAgent.agent_id) : (chatData.agent_id != null ? String(chatData.agent_id) : undefined),
        assignedAgentName: assignedAgent?.agent_name || undefined,
        assignedAgentAvatar: assignedAgent?.agent_avatar || undefined,
      }

      setChat(mappedChat)
      aiActiveRef.current = mappedChat.aiState === 'active'
      setMessages(mappedMessages)
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load chat'))
    } finally {
      setLoading(false)
    }
  }, [chatId])

  useEffect(() => {
    fetchChat()
  }, [fetchChat])

  useEffect(() => {
    if (!socket || !chatId) return

    // Join the specific chat room so we receive messages for this conversation.
    // ALSO re-join on every reconnect — socket.io keeps the same JS object
    // across reconnects, so this effect doesn't re-run when the transport
    // drops and comes back (e.g. after an API server restart). Without the
    // rejoin below, the server-side room membership is lost and the agent
    // stops receiving both `message_received` and `typing_preview` events
    // until the page is refreshed. That presented as "I have to refresh to
    // see new messages" and "live typing preview stopped working".
    const joinRoom = () => socket.emit('join_chat', { chatId })
    joinRoom()
    socket.on('connect', joinRoom)
    socket.on('reconnect', joinRoom)

    // CATCH-UP: re-joining the room only makes us receive messages sent from now
    // on. Any message that arrived DURING the disconnect window (a network blip,
    // sleep/wake, an API restart) was never delivered, so the thread would look
    // stale until a manual refresh. On every RE-connect, re-fetch the thread to
    // pull in whatever we missed. We skip the very first connect (the mount
    // fetchChat already loaded it) by seeding from the socket's current state.
    let hasConnectedOnce = socket.connected
    const catchUp = () => {
      if (!hasConnectedOnce) { hasConnectedOnce = true; return }
      fetchChat()
    }
    socket.on('connect', catchUp)
    socket.on('reconnect', catchUp)

    const handleNewMessage = (msg: any) => {
      // Server emits snake_case — map to our ChatMessage format
      const msgChatId = msg.chat_id || msg.chatId || ''
      // String-coerce both sides: msgChatId comes through as a number from
      // MySQL's auto-increment column, but chatId from the URL is a string.
      // Strict equality (!==) would drop every live message after the first.
      // (Same defensive cast is used by handleTypingPreview below.)
      if (String(msgChatId) !== String(chatId)) return

      const isSystemMsg = msg.message_type === 'system' || msg.sender_type === 'system'
      const mapped: ChatMessage = {
        id: msg.id,
        chatId: msgChatId,
        sender: isSystemMsg ? 'system' : (msg.sender_type || msg.sender || 'customer'),
        senderName: isSystemMsg ? '' : (msg.sender_name || msg.senderName || (msg.sender_type === 'agent' ? 'Agent' : 'Customer')),
        senderId: msg.sender_id != null ? String(msg.sender_id) : undefined,
        senderAvatar: msg.sender_avatar || msg.senderAvatar || undefined,
        content: msg.body || msg.content || '',
        attachments: (msg.attachments || []).map((a: any) => ({
          id: a.id, url: a.url, filename: a.name || a.filename || 'file', type: a.type || 'file', size: a.size || 0,
        })),
        isInternal: msg.is_internal || false,
        createdAt: msg.created_at || msg.createdAt || new Date().toISOString(),
        readAt: msg.read_at || msg.readAt,
      }

      // Avoid duplicate messages (e.g. if we already added it from sendMessage response)
      setMessages((prev) => {
        if (prev.some((m) => m.id === mapped.id)) return prev
        return [...prev, mapped]
      })

      // Play short tone when a customer sends a message in the active chat —
      // but NOT while the AI (Jesse) is handling it and we're just observing.
      if (mapped.sender === 'customer') {
        if (!aiActiveRef.current) playMessageAlert()
        // Clear typing preview when the customer actually sends a message
        setTypingPreview(null)
      }
    }

    const handleTyping = (data: { agentName?: string; agent_name?: string; chat_id?: string; chatId?: string }) => {
      // typing_start is broadcast to the chat room, and our socket may still be a
      // member of OTHER chat rooms — so only show the indicator when the event is
      // for the chat we're actually viewing. (Without this, another agent typing
      // in a different chat briefly showed here.) The legacy per-chat event
      // `chat:<id>:typing` carries no id but is already scoped by its name.
      const incoming = data.chat_id ?? data.chatId
      if (incoming != null && String(incoming) !== String(chatId)) return
      setTypingIndicator(data.agentName || data.agent_name || 'Someone')
      setTimeout(() => setTypingIndicator(null), 3000)
    }

    // Only close the thread we're actually viewing. The payload may be a
    // string id, {chat_id}, {chatId}, or absent (the legacy name-scoped
    // `chat:<id>:closed` event carries none — already scoped by its name).
    // Without this filter, ANY broadcast chat_closed (e.g. the auto-close
    // sweep's first catch-up burst) marked whatever chat was on screen as
    // closed for a few seconds until the next refetch.
    const handleChatClosed = (payload?: any) => {
      const id = typeof payload === 'string' ? payload : (payload?.chat_id ?? payload?.chatId)
      if (id != null && String(id) !== String(chatId)) return
      setChat((prev) => (prev ? { ...prev, status: 'closed' } : null))
    }

    // Keep the "handled by" indicator in sync when someone picks up or the chat
    // is transferred. The server emits this in two shapes (chat-room snake_case
    // and queue-room camelCase) — accept both.
    const handleChatAssigned = (data: any) => {
      const dChatId = data.chat_id ?? data.chatId
      if (dChatId != null && String(dChatId) !== String(chatId)) return
      const agentId = data.agent_id ?? data.agentId
      const agentName = data.agent_name ?? data.agentName
      const agentAvatar = data.agent_avatar ?? data.agentAvatar
      setChat((prev) => prev ? {
        ...prev,
        status: prev.status === 'closed' ? prev.status : 'open',
        assignedAgentId: agentId != null ? String(agentId) : prev.assignedAgentId,
        assignedAgentName: agentName || prev.assignedAgentName,
        assignedAgentAvatar: agentAvatar ?? prev.assignedAgentAvatar,
      } : prev)
    }

    // An agent (possibly on another device) corrected a message — swap it in place
    // and flag it so "View versions" appears.
    const handleMessageEdited = (data: any) => {
      const dChatId = data?.chat_id ?? data?.chatId
      if (dChatId != null && String(dChatId) !== String(chatId)) return
      const mid = String(data?.message_id ?? data?.messageId ?? '')
      if (!mid) return
      setMessages((prev) =>
        prev.map((m) => (String(m.id) === mid ? { ...m, content: String(data?.body ?? ''), edited: true } : m))
      )
    }

    const handleTypingPreview = (data: { chatId?: string; chat_id?: string; text: string }) => {
      // Only ever show a preview belonging to the chat we're actually looking at.
      const incomingChatId = data.chatId ?? data.chat_id
      if (incomingChatId == null || String(incomingChatId) !== String(chatId)) return
      setTypingPreview(data.text || null)

      // Expire it if the customer goes quiet without sending. This was never
      // implemented (just a TODO comment), so a preview could sit on screen
      // indefinitely — and a stale one is what bled into other chats.
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
      previewTimerRef.current = setTimeout(() => setTypingPreview(null), 5000)
    }

    // Listen for the actual event names the server emits
    socket.on('message_received', handleNewMessage)
    socket.on(`chat:${chatId}:new_message`, handleNewMessage) // keep legacy for compatibility
    socket.on(`chat:${chatId}:typing`, handleTyping)
    socket.on('typing_start', handleTyping)
    socket.on(`chat:${chatId}:closed`, handleChatClosed)
    socket.on('chat_closed', handleChatClosed)
    socket.on('typing_preview', handleTypingPreview)
    socket.on('message_edited', handleMessageEdited)
    socket.on('chat_assigned', handleChatAssigned)

    return () => {
      socket.off('message_received', handleNewMessage)
      socket.off(`chat:${chatId}:new_message`, handleNewMessage)
      socket.off(`chat:${chatId}:typing`, handleTyping)
      socket.off('typing_start', handleTyping)
      socket.off(`chat:${chatId}:closed`, handleChatClosed)
      socket.off('chat_closed', handleChatClosed)
      socket.off('typing_preview', handleTypingPreview)
      socket.off('message_edited', handleMessageEdited)
      socket.off('chat_assigned', handleChatAssigned)
      socket.off('connect', joinRoom)
      socket.off('reconnect', joinRoom)
      socket.off('connect', catchUp)
      socket.off('reconnect', catchUp)
      // Leave the chat room when switching away / unmounting, so this socket stops
      // receiving this chat's events (typing_start, typing_preview, messages).
      // This is what prevents typing/preview from one chat leaking into another.
      socket.emit('leave_chat', { chatId })
    }
  }, [socket, chatId, fetchChat])

  // Safety net independent of the socket: whenever the agent returns to the tab
  // (or refocuses the window), re-fetch the thread. This guarantees a fresh view
  // even if the socket missed something while the tab was backgrounded — without
  // the agent ever having to hit refresh.
  useEffect(() => {
    if (!chatId) return
    const refresh = () => { if (document.visibilityState === 'visible') fetchChat() }
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [chatId, fetchChat])

  const sendMessage = useCallback(
    async (content: string, attachments?: any[]) => {
      if (!chatId) return

      try {
        const response = await apiPost<any>(`/chats/${chatId}/messages`, {
          content,
          attachments: attachments || []
        })

        // Map the server response (snake_case) to our ChatMessage format and add to state
        const newMessage: ChatMessage = {
          id: response.id,
          chatId: response.chat_id || chatId,
          sender: response.sender_type || 'agent',
          senderName: response.sender_name || me?.name || 'Agent',
          senderId: response.sender_id != null ? String(response.sender_id) : (me?.id != null ? String(me.id) : undefined),
          senderAvatar: response.sender_avatar || (me as any)?.avatar_url || (me as any)?.avatarUrl || undefined,
          content: response.body || response.content || content,
          attachments: (response.attachments || []).map((a: any) => ({
            id: a.id, url: a.url, filename: a.name || a.filename || 'file', type: a.type || 'file', size: a.size || 0,
          })),
          isInternal: false,
          createdAt: response.created_at || response.createdAt || new Date().toISOString(),
          readAt: undefined,
        }
        // Avoid duplicates — the socket listener may have already added this message
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMessage.id)) return prev
          return [...prev, newMessage]
        })
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to send message'))
      }
    },
    [chatId]
  )

  const suggestReply = useCallback(async () => {
    if (!chatId) return

    try {
      const data = await apiPost<{ suggestion: string }>(`/chats/${chatId}/suggest-reply`)
      setSuggestedReply(data.suggestion)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to get suggestion'))
    }
  }, [chatId])

  const closeChat = useCallback(
    async (resolutionTag: string, notes?: string) => {
      if (!chatId) return

      try {
        // API expects snake_case keys (see closeChatBody schema + chat.routes
        // POST /chats/:id/close). Sending camelCase silently drops the data:
        // Zod strips unknown keys, so the chat closed but resolution_tag and
        // notes were never persisted (resolution_summary/tags came back null).
        await apiPost(`/chats/${chatId}/close`, {
          resolution_tag: resolutionTag,
          resolution_summary: notes,
        })
        setChat((prev) => (prev ? { ...prev, status: 'closed', resolutionTag } : null))
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to close chat'))
      }
    },
    [chatId]
  )

  const addNote = useCallback(
    async (content: string) => {
      if (!chatId) return

      try {
        const raw = await apiPost<any>(`/chats/${chatId}/notes`, { content })
        const newNote = {
          id: String(raw?.id ?? Date.now()),
          content: raw?.content || raw?.body || content,
          authorName: raw?.authorName || raw?.agent_name || 'You',
          createdAt: raw?.createdAt || raw?.created_at || new Date().toISOString(),
        }
        setChat((prev) =>
          prev
            ? {
                ...prev,
                notes: [...prev.notes, newNote]
              }
            : null
        )
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to add note'))
      }
    },
    [chatId]
  )

  const acceptSuggestion = useCallback(async () => {
    if (!chatId || !suggestedReply) return

    try {
      await apiPost(`/chats/${chatId}/analytics`, {
        event: 'suggestion_accepted'
      })
      setSuggestedReply(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to log suggestion'))
    }
  }, [chatId, suggestedReply])

  const discardSuggestion = useCallback(async () => {
    if (!chatId) return

    try {
      await apiPost(`/chats/${chatId}/analytics`, {
        event: 'suggestion_discarded'
      })
      setSuggestedReply(null)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to log suggestion'))
    }
  }, [chatId])

  /**
   * Edit a message you sent. The customer's copy silently updates to the new text
   * (no "edited" marker on their side); every version is kept server-side as an
   * internal record.
   */
  const editMessage = useCallback(
    async (messageId: string, body: string) => {
      if (!chatId) return
      const res = await apiPut<any>(`/chats/${chatId}/messages/${messageId}`, { body })
      const next = String(res?.body ?? body)
      setMessages((prev) =>
        prev.map((m) => (String(m.id) === String(messageId) ? { ...m, content: next, edited: true } : m))
      )
    },
    [chatId]
  )

  /** Full edit timeline for a message (agents only). */
  const getMessageVersions = useCallback(
    async (messageId: string): Promise<MessageVersion[]> => {
      if (!chatId) return []
      const res = await apiGet<{ versions: MessageVersion[] }>(`/chats/${chatId}/messages/${messageId}/versions`)
      return res?.versions || []
    },
    [chatId]
  )

  // Emit typing events so the widget shows a typing indicator for this agent
  const startTyping = useCallback(() => {
    if (!socket || !chatId) return
    socket.emit('typing_start', { chatId, chat_id: chatId })
  }, [socket, chatId])

  const stopTyping = useCallback(() => {
    if (!socket || !chatId) return
    socket.emit('typing_stop', { chatId, chat_id: chatId })
  }, [socket, chatId])

  return {
    chat,
    messages,
    loading,
    error,
    typingIndicator,
    typingPreview,
    suggestedReply,
    sendMessage,
    editMessage,
    getMessageVersions,
    suggestReply,
    closeChat,
    addNote,
    acceptSuggestion,
    discardSuggestion,
    startTyping,
    stopTyping,
  }
}
